/**
 * Policy deep-link path helpers.
 *
 * Policy pages are scoped by route: organization routes live under
 * `/{organizationId}/policies/…` and personal routes under
 * `/dashboard/policies/…`. Centralizing the construction here keeps the
 * Extension Security (Custom Capability Rules / Built-in Detection Catalog) and
 * AI Instruction Catalog deep links from drifting if the URL scheme changes.
 */

/**
 * Route slug for the AI Agent Instructions "Manage catalog" sub-page (KIR-3816).
 * Single-sourced so the `<Route path>` and the "Manage catalog" link can't drift.
 * Matches the backend `agent_instructions` mount (`CATALOG_ROUTE_SEGMENT`).
 */
export const AI_AGENT_INSTRUCTIONS_SLUG = 'ai-agent-instructions';

/**
 * Route slugs for the Policies sidebar sub-pages (KIR-3982), single-sourced so
 * the `<Route path>` and sidebar links can't drift. `ai-instructions` is
 * distinct from {@link AI_AGENT_INSTRUCTIONS_SLUG} (the catalog "Manage" page).
 */
export const POLICIES_AI_INSTRUCTIONS_SLUG = 'ai-instructions';
export const POLICIES_ALLOW_BLOCK_SLUG = 'allow-block-lists';

/**
 * Route slugs for the Extension Security catalog sub-pages, reached from the
 * Detection Policies list. Like the AI Agent Instructions "Manage catalog" page,
 * these have no sidebar entry of their own — they belong to the Detection
 * Policies child, which claims them via `activePaths` so the "Policies" parent
 * doesn't grab the sidebar highlight there (KIR-4184). Single-sourced so the
 * `<Route path>` and the sidebar's active-path match can't drift.
 */
export const EXTENSION_SECURITY_CATALOG_SLUG = 'extension-security-catalog';
export const EXTENSION_SECURITY_CUSTOM_RULES_SLUG =
  'extension-security-custom-rules';

/**
 * Route slug for the "Edit Secret Detection Rules" page (KIR-4590). The slug
 * keeps its original name so existing deep links stay valid. Like the Extension
 * Security sub-pages it has no sidebar entry and is claimed via `activePaths`.
 * Reached from the policy card and from an alert's Manage-rule action.
 */
export const SECRET_DETECTION_GITLEAKS_RULES_SLUG =
  'secret-detection-gitleaks-rules';

/** Query param naming the single gitleaks rule to filter the editor to. */
export const GITLEAKS_RULE_PARAM = 'rule';

/** Base policies path for the current scope. */
export function policiesBasePath(organizationId?: string): string {
  return organizationId ? `/${organizationId}/policies` : '/dashboard/policies';
}

/**
 * Query param naming the policy scope being edited (KIR-3800). Absent means the
 * organization scope; a security group id selects that group.
 *
 * It is a query param rather than a path segment so every existing policies
 * sub-route (`ai-instructions`, `allow-block-lists`, the catalog pages) keeps
 * working unchanged, and so a scope switch doesn't invalidate a deep link.
 */
export const POLICY_SCOPE_PARAM = 'scope';

/**
 * Policies page URL pre-set to a security group's scope — the "Edit Policies"
 * action on the group pages (AC #12).
 */
export function groupPoliciesPath(
  organizationId: string,
  groupId: string
): string {
  return `${policiesBasePath(organizationId)}?${POLICY_SCOPE_PARAM}=${encodeURIComponent(groupId)}`;
}

/** A sub-page under the scoped policies path (e.g. `extension-security-catalog`). */
export function policySubPath(
  organizationId: string | undefined,
  slug: string
): string {
  return `${policiesBasePath(organizationId)}/${slug}`;
}

/**
 * Page-header title for a Policies sub-page (KIR-3982), mirroring Shadow AI's
 * "Shadow AI / Tools". Returns `null` for anything that isn't one of the three
 * sub-pages (e.g. the catalog "Manage" routes) so callers fall back to the plain
 * tab label. Shared by the org view and the personal dashboard so the two can't
 * drift as sub-pages are added.
 *
 * "Allow/Block Lists" is spelled as on the page's group heading — the sidebar
 * label uses spaced slashes, which would read as four segments in the title.
 */
export function policiesPageTitle(pathname: string): string | null {
  const path = pathname.replace(/\/+$/, '');
  if (path.endsWith(`/${POLICIES_ALLOW_BLOCK_SLUG}`)) {
    return 'Policies / Allow/Block Lists';
  }
  if (path.endsWith(`/${POLICIES_AI_INSTRUCTIONS_SLUG}`)) {
    return 'Policies / AI Agent Instructions';
  }
  if (path.endsWith('/policies')) {
    return 'Policies / Detection Policies';
  }
  return null;
}

/**
 * Copies the active {@link POLICY_SCOPE_PARAM} from `search` onto a policies
 * URL. The scope selector lives on the Policies tab and every sub-page shares
 * that tab's scope-derived query/mutation hooks, so a link that drops the param
 * silently moves a security-group edit to the organization.
 */
export function withPolicyScope(
  path: string,
  search: string | URLSearchParams
): string {
  const scope = new URLSearchParams(search).get(POLICY_SCOPE_PARAM);
  if (!scope) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${POLICY_SCOPE_PARAM}=${encodeURIComponent(scope)}`;
}
